import type * as THREE from "three";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";

const DIAMETER_PX = 10;
const BORDER_WIDTH_PX = 2;
const DEFAULT_HIGHLIGHT_COLOR = "#534ab7";

/** */
class VertexHandle {
	public readonly sceneObject: CSS2DObject;
	private readonly handleElement: HTMLDivElement;

	private color: string;
	private fillColor: string;
	private highlightColor: string;
	private isHighlighted = false;

	constructor({
		color,
		fillColor = "#ffffff",
		highlightColor = DEFAULT_HIGHLIGHT_COLOR,
	}: {
		color: string;
		fillColor?: string;
		highlightColor?: string;
	}) {
		this.color = color;
		this.fillColor = fillColor;
		this.highlightColor = highlightColor;

		const anchorElement = document.createElement("div");
		anchorElement.style.position = "relative";

		this.handleElement = document.createElement("div");
		Object.assign(this.handleElement.style, {
			position: "absolute",
			left: "0",
			top: "0",
			width: `${DIAMETER_PX}px`,
			height: `${DIAMETER_PX}px`,
			boxSizing: "border-box",
			borderWidth: `${BORDER_WIDTH_PX}px`,
			borderStyle: "solid",
			borderColor: color,
			backgroundColor: fillColor,
			borderRadius: "50%",
			transform: "translate(-50%, -50%)",
			pointerEvents: "none",
			transition: "opacity 0.2s linear, background-color 0.1s linear, border-color 0.1s linear",
			opacity: "1",
		} satisfies Partial<CSSStyleDeclaration>);

		anchorElement.append(this.handleElement);
		this.sceneObject = new CSS2DObject(anchorElement);
	}

	public update({ position, isVisible, isHighlighted }: { position?: THREE.Vector3; isVisible?: boolean; isHighlighted?: boolean }): void {
		if (position !== undefined) {
			this.sceneObject.position.copy(position);
		}

		if (isVisible !== undefined) {
			this.handleElement.style.opacity = isVisible ? "1" : "0";
		}

		if (isHighlighted !== undefined) {
			this.isHighlighted = isHighlighted;
			this.applyColors();
		}
	}

	public setColors({ color, fillColor, highlightColor }: { color?: string; fillColor?: string; highlightColor?: string }): void {
		if (color !== undefined) {
			this.color = color;
		}

		if (fillColor !== undefined) {
			this.fillColor = fillColor;
		}

		if (highlightColor !== undefined) {
			this.highlightColor = highlightColor;
		}

		this.applyColors();
	}

	private applyColors(): void {
		// Same size, only colour — a handle that grew would shift the apparent vertex position.
		this.handleElement.style.borderColor = this.isHighlighted ? this.highlightColor : this.color;
		this.handleElement.style.backgroundColor = this.isHighlighted ? this.highlightColor : this.fillColor;
	}

	public dispose(): void {
		this.sceneObject.element.remove();
		this.sceneObject.removeFromParent();
	}
}

export { VertexHandle };
